import { useState, useEffect, useRef, useMemo } from 'react';
import styles from './DatePicker.module.css';

const DatePicker = ({
    value,
    onChange,
    placeholder = 'Selecione',
    className = '',
    availableDates = null // Array de datas no formato 'YYYY-MM-DD' que estão disponíveis
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date(value || new Date()));
    const containerRef = useRef(null);

    // Criar um Set para busca O(1) das datas disponíveis
    const availableDatesSet = useMemo(() => {
        if (!availableDates || !Array.isArray(availableDates)) return null;
        return new Set(availableDates);
    }, [availableDates]);

    // Fechar ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Dias da semana
    const diasSemana = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

    // Gerar dias do mês atual
    const gerarDiasDoMes = () => {
        const ano = currentMonth.getFullYear();
        const mes = currentMonth.getMonth();

        const primeiroDia = new Date(ano, mes, 1);
        const ultimoDia = new Date(ano, mes + 1, 0);

        const diasAnteriores = primeiroDia.getDay();
        const totalDias = ultimoDia.getDate();

        const dias = [];

        // Dias do mês anterior (para preencher o início)
        const mesAnteriorUltimoDia = new Date(ano, mes, 0).getDate();
        for (let i = diasAnteriores - 1; i >= 0; i--) {
            dias.push({
                dia: mesAnteriorUltimoDia - i,
                mesAtual: false,
                data: new Date(ano, mes - 1, mesAnteriorUltimoDia - i)
            });
        }

        // Dias do mês atual
        for (let i = 1; i <= totalDias; i++) {
            dias.push({
                dia: i,
                mesAtual: true,
                data: new Date(ano, mes, i)
            });
        }

        // Dias do próximo mês (para preencher o final)
        const diasRestantes = 42 - dias.length; // 6 semanas * 7 dias
        for (let i = 1; i <= diasRestantes; i++) {
            dias.push({
                dia: i,
                mesAtual: false,
                data: new Date(ano, mes + 1, i)
            });
        }

        return dias;
    };

    // Verificar se uma data está disponível (tem vendas)
    const isDataDisponivel = (data) => {
        // Se não tiver lista de datas disponíveis, todas estão disponíveis
        if (!availableDatesSet) return true;

        const dataStr = data.toISOString().split('T')[0];
        return availableDatesSet.has(dataStr);
    };

    // Verificar se é a data selecionada
    const isSelecionado = (data) => {
        return data.toISOString().split('T')[0] === value;
    };

    // Verificar se é hoje
    const isHoje = (data) => {
        const hoje = new Date();
        return data.toDateString() === hoje.toDateString();
    };

    // Formatar mês/ano para exibição
    const formatarMesAno = () => {
        return currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    };

    // Navegar entre meses
    const mesAnterior = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const proximoMes = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    // Selecionar data
    const selecionarData = (data, disabled) => {
        if (disabled) return; // Não permitir seleção de data desabilitada
        const dataStr = data.toISOString().split('T')[0];
        onChange(dataStr);
        setIsOpen(false);
    };

    // Ir para hoje (apenas se hoje tiver vendas, senão vai para última data disponível)
    const irParaHoje = () => {
        const hoje = new Date();
        const hojeStr = hoje.toISOString().split('T')[0];

        if (availableDatesSet && !availableDatesSet.has(hojeStr)) {
            // Se hoje não tem vendas, vai para a data mais recente com vendas
            if (availableDates && availableDates.length > 0) {
                const ultimaDataComVendas = availableDates[0]; // Já ordenado DESC
                onChange(ultimaDataComVendas);
                const [ano, mes] = ultimaDataComVendas.split('-');
                setCurrentMonth(new Date(parseInt(ano), parseInt(mes) - 1, 1));
            }
        } else {
            setCurrentMonth(hoje);
            onChange(hojeStr);
        }
        setIsOpen(false);
    };

    // Limpar seleção
    const limpar = () => {
        onChange('');
        setIsOpen(false);
    };

    // Formatar data para exibição no input
    const formatarDataExibicao = () => {
        if (!value) return '';
        const [ano, mes, dia] = value.split('-');
        return `${dia}/${mes}/${ano}`;
    };

    return (
        <div className={`${styles.container} ${className}`} ref={containerRef}>
            <div
                className={styles.inputContainer}
                onClick={() => setIsOpen(!isOpen)}
            >
                <input
                    type="text"
                    value={formatarDataExibicao()}
                    readOnly
                    placeholder={placeholder}
                    className={styles.input}
                />
                <svg
                    className={styles.calendarIcon}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            </div>

            {isOpen && (
                <div className={styles.calendar}>
                    {/* Header do calendário */}
                    <div className={styles.calendarHeader}>
                        <span className={styles.mesAno}>{formatarMesAno()}</span>
                        <div className={styles.navButtons}>
                            <button onClick={mesAnterior} className={styles.navBtn} type="button">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                            </button>
                            <button onClick={proximoMes} className={styles.navBtn} type="button">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Dias da semana */}
                    <div className={styles.diasSemana}>
                        {diasSemana.map((dia, idx) => (
                            <span key={idx} className={styles.diaSemana}>{dia}</span>
                        ))}
                    </div>

                    {/* Grid de dias */}
                    <div className={styles.diasGrid}>
                        {gerarDiasDoMes().map((item, idx) => {
                            const selecionado = isSelecionado(item.data);
                            const hoje = isHoje(item.data);
                            const disponivel = isDataDisponivel(item.data);
                            const disabled = !disponivel && item.mesAtual;

                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => selecionarData(item.data, disabled)}
                                    disabled={disabled}
                                    className={`
                                        ${styles.dia}
                                        ${!item.mesAtual ? styles.outroMes : ''}
                                        ${selecionado ? styles.selecionado : ''}
                                        ${hoje ? styles.hoje : ''}
                                        ${disabled ? styles.disabled : ''}
                                    `}
                                    title={disabled ? 'Sem vendas nesta data' : ''}
                                >
                                    {item.dia}
                                </button>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className={styles.calendarFooter}>
                        <button onClick={limpar} className={styles.footerBtn} type="button">Limpar</button>
                        <button onClick={irParaHoje} className={styles.footerBtnHoje} type="button">
                            {availableDatesSet && availableDates?.length > 0 ? 'Última Data' : 'Hoje'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;
